// Utility helpers — pure functions only. No React, no Firebase.
// Used by components, hooks, and stores.

import { format, formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

export const cn = (...classes: Array<string | undefined | null | false | 0>): string =>
  classes.filter((c): c is string => Boolean(c)).join(' ');

/** Format IDR currency: 1500000 -> "Rp 1.500.000" */
export const formatIDR = (amount: number): string => {
  if (Number.isNaN(amount) || !Number.isFinite(amount)) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/** Format plain number with thousand separators */
export const formatNumber = (n: number): string =>
  new Intl.NumberFormat('id-ID').format(n);

/**
 * True when the value can be safely formatted as a date. Defends against
 * 0 epoch (the "1 Jan 1970" trap from broken `serverTimestamp()` writes
 * — see memory `stripUndefined` mangling Firestore sentinels) and other
 * nonsense inputs that may slip through. Use as a guard before
 * `formatDateTime` / `formatDate` / `formatRelative`.
 */
export function isValidDateMs(ms: unknown): ms is number {
  return typeof ms === 'number' && Number.isFinite(ms) && ms > 0;
}

/** Format date: epoch ms -> "9 Jun 2026, 20:30". Returns '—' for invalid. */
export const formatDateTime = (ms: number | undefined | null): string => {
  if (!isValidDateMs(ms)) return '—';
  return format(ms, 'd MMM yyyy, HH:mm', { locale: idLocale });
};

/** Format date only: epoch ms -> "9 Jun 2026". Returns '—' for invalid. */
export const formatDate = (ms: number | undefined | null): string => {
  if (!isValidDateMs(ms)) return '—';
  return format(ms, 'd MMM yyyy', { locale: idLocale });
};

/** Relative time: "2 hours ago". Returns '—' for invalid. */
export const formatRelative = (ms: number | undefined | null): string => {
  if (!isValidDateMs(ms)) return '—';
  return formatDistanceToNow(ms, { addSuffix: true, locale: idLocale });
};

/** Truncate long string with ellipsis */
export const truncate = (s: string, n: number): string =>
  s.length > n ? `${s.slice(0, n - 1)}…` : s;

/** Generate short ID for invoice number, event code, etc. */
export const generateCode = (prefix: string, ms: number = Date.now()): string => {
  const datePart = format(ms, 'yyyyMMdd');
  const rand = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, '0');
  return `${prefix}-${datePart}-${rand}`;
};

/** Safe parse of JSON */
export const safeJson = <T>(s: string | null, fallback: T): T => {
  if (!s) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
};

// ── Calendar dates ───────────────────────────────────────────────────────────
//
// An expiry date is a calendar day, not an instant, but it travels through the
// app as an ms timestamp because that is what the domain types use. These two
// are exact inverses and BOTH work in UTC — which is the whole point.
//
// Getting this wrong is not a cosmetic bug. Mixing a local-midnight timestamp
// with a UTC read shifts the date back a day east of Greenwich, and because a
// batch is identified by (product, lot, expiry), a shifted date silently
// creates a second batch instead of updating the one that exists. That is
// exactly what happened during the stock-import build: `new Date('2027-06-30' +
// 'T00:00:00')` is local midnight, and `.toISOString()` turned it into
// 2027-06-29 in Jakarta.
//
// Always route calendar dates through these; never hand-roll the conversion.

/** 'yyyy-mm-dd' → ms at UTC midnight. */
export const isoDateToMs = (iso: string): number => new Date(`${iso}T00:00:00Z`).getTime();

/** ms → 'yyyy-mm-dd', read in UTC so it round-trips isoDateToMs exactly. */
export const msToIsoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
